import {
  Bill,
  Item,
  PaymentDueInfo,
  PaymentDueStatus,
  StatementStatus,
} from "../models";

const statusOrder = [
  StatementStatus.PAST_DUE,
  StatementStatus.FINAL_NOTICE,
  StatementStatus.PRE_COLLECTION,
  StatementStatus.READY_TO_PAY,
  StatementStatus.PARTIAL_PENDING,
  StatementStatus.PAYMENT_PLAN,
  StatementStatus.PAYMENT_PENDING,
];

const priorityMap: Record<PaymentDueStatus, number> = {
  [PaymentDueStatus.DUE_NOW]: 0,
  [PaymentDueStatus.DUE_UPON_RECEIPT]: 1,
  [PaymentDueStatus.VALID_DATE]: 2,
  [PaymentDueStatus.EMPTY]: 3,
};

const getPriority = (item: Bill): number => {
  const paymentInfo =
    (item?.dueDate as PaymentDueInfo).pastDueExists ??
    PaymentDueStatus.VALID_DATE;
  return priorityMap[paymentInfo] ?? 4;
};

const getSortableDate = (item: Bill): number => {
  const paymentInfo = item?.dueDate as PaymentDueInfo;
  const itemDueDate = paymentInfo?.dueDate ?? (item?.dueDate as string);

  return itemDueDate
    ? new Date(itemDueDate).getTime()
    : Number.MAX_SAFE_INTEGER;
};

const getStatus = (item: Bill): StatementStatus => {
  return "currentInstallmentIndex" in item
    ? StatementStatus.PAYMENT_PLAN
    : (item as Item).statementStatus;
};

const getStatusIndex = (item: Bill): number =>
  statusOrder.indexOf(getStatus(item));

const compareBills = (a: Bill, b: Bill): number => {
  //If the item is payment pending, it goes bottom of the list
  const aIsPending = getStatus(a) === StatementStatus.PAYMENT_PENDING;
  const bIsPending = getStatus(b) === StatementStatus.PAYMENT_PENDING;

  if (aIsPending && !bIsPending) return 1;
  if (!aIsPending && bIsPending) return -1;

  //Get Due date priorities
  const priorityA = getPriority(a);
  const priorityB = getPriority(b);

  //if the both are valid date, compare the due dates
  const bothAreValidDate = priorityA === 2 && priorityB === 2;

  if (!bothAreValidDate && priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  if (bothAreValidDate) {
    const dateA = getSortableDate(a);
    const dateB = getSortableDate(b);

    if (dateA !== dateB) {
      return dateA - dateB;
    }
  }

  // If the priorities and due dates are the same, compare statement statuses
  return getStatusIndex(a) - getStatusIndex(b);
};

export const orderReadyBills = (bills: Bill[]): Bill[] => {
  if (!bills) return [];

  return [...bills].sort(compareBills);
};

export const orderUpcomingBills = (bills: Item[]): Item[] => {
  if (!bills) return [];

  return bills.sort((a: Item, b: Item) => {
    const firstDate = a?.dateOfService
      ? new Date(a.dateOfService).getTime()
      : 0;
    const secondDate = b?.dateOfService
      ? new Date(b.dateOfService).getTime()
      : 0;

    const isClaimPendingA = a.statementStatus === StatementStatus.CLAIM_PENDING;
    const isClaimPendingB = b.statementStatus === StatementStatus.CLAIM_PENDING;

    if (isClaimPendingA && !isClaimPendingB) return -1;
    if (!isClaimPendingA && isClaimPendingB) return 1;

    return firstDate - secondDate;
  });
};
